import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'

import { hasAttachmentWithCaption } from '@/utils/chat-message-content'
import { MessageSeriesPosition } from '@/utils/chat-message-series'

import { ChatMessageFileContentItem, ChatMessageImageContentItem, ChatMessageVideoContentItem, FileMessage } from './chat-messages'
import { MessageAttachmentTextSplit } from './message-attachment-text-split'

import { Message } from '@/types/chat.type'

interface MobileEmojiContentRendererProps {
  content: string
  isEmojiOnly: boolean
  isOwnMessage: boolean
}

interface MobileMessageBodyContentProps {
  message: Message
  fileMessage: FileMessage | null
  isOwnMessage: boolean
  isEmojiOnlyMessage: boolean
  seriesPosition: MessageSeriesPosition
  bubbleClassName: string
  mentionClassName: string
  MobileEmojiContentRenderer: React.ComponentType<MobileEmojiContentRendererProps>
  extras?: React.ReactNode
}

export const MobileMessageBodyContent: React.FC<MobileMessageBodyContentProps> = ({
  message,
  fileMessage,
  isOwnMessage,
  isEmojiOnlyMessage,
  seriesPosition,
  bubbleClassName,
  mentionClassName,
  MobileEmojiContentRenderer,
  extras,
}) => {
  const hasAttachmentAndCaption = hasAttachmentWithCaption(message.type, message.content || '', message.fileName)

  const renderFileAttachment = () => {
    if (!fileMessage) return null

    if (fileMessage.isImage) {
      return <ChatMessageImageContentItem fileData={fileMessage} />
    }
    if (fileMessage.isVideo) {
      return <ChatMessageVideoContentItem fileData={fileMessage} />
    }
    return <ChatMessageFileContentItem fileData={fileMessage} />
  }

  const renderTextBody = () => {
    if (isEmojiOnlyMessage) {
      return (
        <MobileEmojiContentRenderer
          content={message.content}
          isEmojiOnly={isEmojiOnlyMessage}
          isOwnMessage={isOwnMessage}
        />
      )
    }

    return (
      <div
        className={`text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word] hyphens-auto ${mentionClassName}`}
        dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(message.content) }}
      />
    )
  }

  if (hasAttachmentAndCaption && fileMessage) {
    return (
      <MessageAttachmentTextSplit
        isOwnMessage={isOwnMessage}
        seriesPosition={seriesPosition}
        attachment={renderFileAttachment()}
        text={renderTextBody()}
        textBubbleClassName={bubbleClassName}
        extras={extras}
      />
    )
  }

  if (fileMessage) {
    return renderFileAttachment()
  }

  return renderTextBody()
}
